import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { useDateTimeFormat } from '../conversions/useDateFormat';

// Reusable truncated cell component with smart tooltip (only shows when actually truncated)
interface TruncatedCellProps {
  children: React.ReactNode;
  text: string;
  className?: string;
}

export const TruncatedCell = ({
  children,
  text,
  className,
}: TruncatedCellProps) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const isOverflowing =
          textRef.current.scrollWidth > textRef.current.clientWidth;
        setShowTooltip(isOverflowing);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [text]);

  const textElement = (
    <div ref={textRef} className={cn('truncate w-full', className)}>
      {children}
    </div>
  );

  if (showTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{textElement}</TooltipTrigger>
        <TooltipContent>
          <p className="max-w-xs break-words">{text}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return textElement;
};

// Date/DateTime cell component that never truncates
interface DateTimeCellProps {
  children: React.ReactNode;
  className?: string;
}

export const DateTimeCell = ({ children, className }: DateTimeCellProps) => {
  return <div className={cn('whitespace-nowrap', className)}>{children}</div>;
};

// Numeric cell component that never truncates and right-aligns
interface NumericCellProps {
  children: React.ReactNode;
  className?: string;
  align?: 'left' | 'right' | 'center';
}

export const NumericCell = ({
  children,
  className,
  align = 'right',
}: NumericCellProps) => {
  const alignmentClass = {
    left: 'text-left',
    right: 'text-right',
    center: 'text-center',
  }[align];

  return (
    <div className={cn('whitespace-nowrap', alignmentClass, className)}>
      {children}
    </div>
  );
};

// Utility to detect {min, max} range objects from the backend
export interface RangeValue {
  min: number;
  max: number;
}

export function isRangeObject(value: unknown): value is RangeValue {
  return (
    value !== null &&
    value !== undefined &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'min' in value &&
    'max' in value &&
    typeof (value as Record<string, unknown>).min === 'number' &&
    typeof (value as Record<string, unknown>).max === 'number'
  );
}

export function getRangeSortValue(value: unknown): number {
  if (isRangeObject(value)) return value.min;
  const num = Number(value);
  return isNaN(num) ? 0 : num;
}

// Cell for rendering numeric range values (min – max)
interface RangeCellProps {
  min: number;
  max: number;
  className?: string;
  unit?: string;
}

export const RangeCell = ({ min, max, className, unit }: RangeCellProps) => {
  const formattedMin = min.toFixed(2);
  const formattedMax = max.toFixed(2);
  const suffix = unit ? ` ${unit}` : '';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={cn('whitespace-nowrap text-right', className)}>
          {formattedMin}–{formattedMax}
          {suffix}
        </div>
      </TooltipTrigger>
      <TooltipContent side="left">
        <p className="text-xs">
          Min: {formattedMin}
          {suffix}
        </p>
        <p className="text-xs">
          Max: {formattedMax}
          {suffix}
        </p>
      </TooltipContent>
    </Tooltip>
  );
};

// Short text cell that displays without truncation
interface ShortTextCellProps {
  children: React.ReactNode;
  className?: string;
}

export const ShortTextCell = ({ children, className }: ShortTextCellProps) => {
  return <div className={cn('', className)}>{children}</div>;
};

// Cell for rendering array/object values as a count with hover details
interface ObjectArrayCellProps {
  value: unknown;
  className?: string;
}

const formatKey = (key: string): string => {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const ISO_DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

type DateFormatter = (date: Date | undefined | null) => string;

const formatEntryValue = (val: unknown, formatter?: DateFormatter): string => {
  if (val === null || val === undefined) return '-';
  if (typeof val === 'string' && ISO_DATETIME_REGEX.test(val)) {
    const date = new Date(val);
    if (!isNaN(date.getTime()) && formatter) return formatter(date);
    return val;
  }
  return String(val);
};

const formatSingleEntry = (
  entry: Record<string, unknown>,
  index: number,
  formatter?: DateFormatter,
): string => {
  const name = entry['shape_name'] ?? entry['name'] ?? '';
  const header = name ? `Pass ${index + 1} — ${name}` : `Pass ${index + 1}`;
  const details = Object.entries(entry)
    .filter(
      ([key]) => key !== 'shape_name' && key !== 'name' && key !== 'order',
    )
    .map(
      ([key, val]) =>
        `  ${formatKey(key)}: ${formatEntryValue(val, formatter)}`,
    )
    .join('\n');
  return `${header}\n${details}`;
};

const formatObjectDetails = (
  value: unknown,
  formatter?: DateFormatter,
): string => {
  if (value === null || value === undefined) return '-';

  if (typeof value !== 'object') return String(value);

  try {
    const entries = (
      Array.isArray(value)
        ? value
        : Object.values(value as Record<string, unknown>)
    ) as Array<Record<string, unknown> & { order?: unknown }>;
    if (entries.length === 0) return '-';
    if (entries.every((e) => typeof e === 'object' && e !== null)) {
      const sorted = [...entries].sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return Number(a.order) - Number(b.order);
        }
        return 0;
      });
      return sorted
        .map((entry, i) =>
          formatSingleEntry(entry as Record<string, unknown>, i, formatter),
        )
        .join('\n\n');
    }

    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export const ObjectArrayCell = ({ value, className }: ObjectArrayCellProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const count = Array.isArray(value)
    ? value.length
    : typeof value === 'object' && value !== null
      ? Object.keys(value).length
      : 1;

  const formattedDetails = useMemo(
    () => formatObjectDetails(value, formatDateTimeWithTimeZone),
    [value, formatDateTimeWithTimeZone],
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn('flex items-center gap-1 cursor-default', className)}
        >
          <span>{count}</span>
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="size-3.5 text-muted-foreground shrink-0"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="left"
        className="max-w-sm max-h-32 overflow-auto p-1"
      >
        <pre className="text-xs p-2 whitespace-pre-wrap break-words">
          {formattedDetails}
        </pre>
      </TooltipContent>
    </Tooltip>
  );
};
